import { RefObject, useState, useEffect, useMemo } from 'react';
import { throttle } from 'underscore';

const useTrackSelectParentSize = ({
  ref,
  pause,
  recalculate,
}: {
  pause?: boolean;
  ref: RefObject<HTMLElement | null>;
  recalculate?: number;
}): DOMRect => {
  /* Tracks bounding rect of element, even on window resize, or when th element itself changes */
  const [rect, setRect] = useState<DOMRect>(new DOMRect(0, 0, 0, 0));

  useEffect(() => {
    const refEl = ref.current;
    if (!refEl || pause) return;

    const updateRect = throttle(() => {
      if (refEl) {
        setRect(refEl.getBoundingClientRect());
      }
    }, 10);

    setRect(refEl.getBoundingClientRect());

    const resizeObserver = new ResizeObserver(() => {
      updateRect();
    });

    resizeObserver.observe(refEl);

    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [pause, ref, recalculate]);

  return useMemo(() => rect, [rect]);
};

export default useTrackSelectParentSize;
